import React from 'react'
import { createPortal } from 'react-dom'
import { getBanks } from '../../../../functions/getDataForSelects/dataFunctions'
import { getCountries } from '../../../../functions/getDataForSelects/getCountries'
import { swalSuccess } from '../../../../functions/swalOptions'
import { useAppDispatch } from '../../../../hooks/redux'
import { type User } from '../../../../store/slices/userSlice'
import { type BsoUnitAnalyticsRow } from '../adminBsoUnitStatistics'
import {
    DEFAULT_MANUAL_OFFER_STATUS,
    isManualActionUserConfigured,
    manualActionUserConfig,
    MANUAL_OFFER_BUYER_TYPES,
    MANUAL_OFFER_CLIENT_TYPES,
    MANUAL_OFFER_DOCUMENT_TYPES,
    MANUAL_OFFER_DURATION_OPTIONS,
    MANUAL_OFFER_OCCUPANT_OPTIONS,
    MANUAL_OFFER_PAYMENT_OPTIONS,
    MANUAL_OFFER_PURCHASE_REASONS,
    MANUAL_OFFER_YES_NO_OPTIONS
} from './manualOperations.config'
import { createManualOffer } from './manualOfferActions'
import { getManualOfferSignedDocumentCategory } from './manualOfferDocuments'
import { ManualUserSelector } from './ManualUserSelector'
import { type ManualOfferBuyerType, type ManualOfferClientType, type ManualOfferDocumentFiles, type ManualOfferDocumentType, type ManualOfferUserMode } from './manualOperations.types'
import './ManualOfferModal.scss'

const getDefaultDateLocal = (): string => {
    const date = new Date()
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
    return date.toISOString().slice(0, 10)
}

const getCurrentYear = (): string => String(new Date().getFullYear())
const formatPrice = (price: number | null): string => price == null ? '-' : `AED ${price.toLocaleString('en-US')}`
const getFileName = (file: File | null): string => file?.name ?? 'No file selected'

const DOCUMENT_TYPE_LABELS: Record<ManualOfferDocumentType, string> = {
    'emirates-id': 'Emirates ID',
    passport: 'Passport',
    both: 'Both'
}

const FileInput = ({
    label,
    file,
    disabled,
    onChange,
    required = false
}: {
    label: string
    file: File | null
    disabled: boolean
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    required?: boolean
}): JSX.Element => (
    <label className="manual-offer__file">
        <span>{label}{required ? ' *' : ''}</span>
        <input type="file" disabled={disabled} onChange={onChange} />
        <small>{getFileName(file)}</small>
    </label>
)

export const ManualOfferModal = ({
    unit,
    users,
    onClose
}: {
    unit: BsoUnitAnalyticsRow | null
    users: User[]
    onClose: () => void
}): JSX.Element | null => {
    const dispatch = useAppDispatch()
    const [userMode, setUserMode] = React.useState<ManualOfferUserMode>('existing-user')
    const [selectedUser, setSelectedUser] = React.useState<User | null>(null)
    const [amount, setAmount] = React.useState('')
    const [processStartDate, setProcessStartDate] = React.useState(getDefaultDateLocal)
    const [processEndDate, setProcessEndDate] = React.useState(getDefaultDateLocal)
    const [clientType, setClientType] = React.useState<ManualOfferClientType>('Individual')
    const [documentType, setDocumentType] = React.useState<ManualOfferDocumentType>('emirates-id')
    const [firstName, setFirstName] = React.useState('')
    const [lastName, setLastName] = React.useState('')
    const [country, setCountry] = React.useState('')
    const [email, setEmail] = React.useState('')
    const [phone, setPhone] = React.useState('')
    const [notes, setNotes] = React.useState('')
    const [occupation, setOccupation] = React.useState('')
    const [employer, setEmployer] = React.useState('')
    const [occupants, setOccupants] = React.useState('Single')
    const [smoking, setSmoking] = React.useState<'Yes' | 'No'>('No')
    const [pets, setPets] = React.useState<'Yes' | 'No'>('No')
    const [petType, setPetType] = React.useState('')
    const [inUAESince, setInUAESince] = React.useState(getCurrentYear)
    const [uaeBank, setUaeBank] = React.useState('')
    const [businessName, setBusinessName] = React.useState('')
    const [businessOwnerName, setBusinessOwnerName] = React.useState('')
    const [businessActivity, setBusinessActivity] = React.useState('')
    const [establishmentDate, setEstablishmentDate] = React.useState(getCurrentYear)
    const [payments, setPayments] = React.useState('1')
    const [contractStartDate, setContractStartDate] = React.useState(getDefaultDateLocal)
    const [duration, setDuration] = React.useState('12')
    const [buyerType, setBuyerType] = React.useState<ManualOfferBuyerType>('Cash Buyer')
    const [bankName, setBankName] = React.useState('')
    const [bankPreApproval, setBankPreApproval] = React.useState<'Yes' | 'No'>('No')
    const [preApprovedAmount, setPreApprovedAmount] = React.useState('')
    const [purchaseReason, setPurchaseReason] = React.useState<'Investment' | 'Personal use'>('Personal use')
    const [documents, setDocuments] = React.useState<ManualOfferDocumentFiles>({
        passport: null,
        emiratesIdFront: null,
        emiratesIdBack: null,
        tradeLicense: null,
        depositSlip: null,
        depositCheque: null,
        signedDocument: null
    })
    const [saving, setSaving] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    React.useEffect(() => {
        if (unit == null) return
        const today = getDefaultDateLocal()
        setUserMode('existing-user')
        setSelectedUser(null)
        setAmount(unit.currentPrice != null ? String(unit.currentPrice) : '')
        setProcessStartDate(today)
        setProcessEndDate(today)
        setClientType('Individual')
        setDocumentType('emirates-id')
        setFirstName('')
        setLastName('')
        setCountry('')
        setEmail('')
        setPhone('')
        setNotes('')
        setOccupation('')
        setEmployer('')
        setOccupants('Single')
        setSmoking('No')
        setPets('No')
        setPetType('')
        setInUAESince(getCurrentYear())
        setUaeBank('')
        setBusinessName('')
        setBusinessOwnerName('')
        setBusinessActivity('')
        setEstablishmentDate(getCurrentYear())
        setPayments('1')
        setContractStartDate(today)
        setDuration('12')
        setBuyerType('Cash Buyer')
        setBankName('')
        setBankPreApproval('No')
        setPreApprovedAmount('')
        setPurchaseReason('Personal use')
        setDocuments({ passport: null, emiratesIdFront: null, emiratesIdBack: null, tradeLicense: null, depositSlip: null, depositCheque: null, signedDocument: null })
        setSaving(false)
        setError(null)
    }, [unit])

    React.useEffect(() => {
        if (unit == null) return
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !saving) onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [unit, saving, onClose])

    const bankOptions = React.useMemo(() => [...getBanks(), 'Other bank - (Add in notes)'], [])
    const countryOptions = React.useMemo(() => getCountries(), [])

    if (unit == null || typeof document === 'undefined') return null

    const manualUserIsConfigured = isManualActionUserConfigured()
    const userId = userMode === 'manual-bso' ? manualActionUserConfig.id : selectedUser?.id
    const amountNumber = Number(amount)
    const listingType = unit.listingType
    const isCompany = clientType === 'Company'
    const isIndividual = clientType === 'Individual'
    const isRent = listingType === 'Rent'
    const isSale = listingType === 'Sale'
    const needsPassport = documentType === 'passport' || documentType === 'both'
    const needsEmiratesId = documentType === 'emirates-id' || documentType === 'both'
    const signedDocumentCategory = listingType != null ? getManualOfferSignedDocumentCategory(listingType) : null
    const manualConfigError = userMode === 'manual-bso' && !manualUserIsConfigured
        ? 'Manual by BSO user is not configured. Add REACT_APP_BSO_MANUAL_ACTION_USER_ID.'
        : null

    const validationError =
        listingType == null
            ? 'Unit listing type must be Rent or Sale.'
            : userMode === 'existing-user' && selectedUser == null
                ? 'Select an existing user.'
                : userMode === 'manual-bso' && !manualUserIsConfigured
                    ? 'Manual by BSO user is not configured.'
                    : !Number.isFinite(amountNumber) || amountNumber <= 0
                        ? 'Offer amount must be greater than zero.'
                        : processStartDate.trim() === '' || processEndDate.trim() === ''
                            ? 'Process start and end dates are required.'
                            : new Date(processEndDate).getTime() < new Date(processStartDate).getTime()
                                ? 'Process end date cannot be before process start date.'
                                : firstName.trim() === '' || lastName.trim() === ''
                                    ? 'Client first and last name are required.'
                                    : country.trim() === ''
                                        ? 'Client nationality is required.'
                                        : isIndividual && isRent && (occupation.trim() === '' || employer.trim() === '' || occupants.trim() === '' || inUAESince.trim() === '' || uaeBank.trim() === '')
                                            ? 'Rent individual offers require occupation, employer, occupants, UAE since, and bank.'
                                            : pets === 'Yes' && petType.trim() === ''
                                                ? 'Pet type is required when client has pets.'
                                                : isCompany && (businessName.trim() === '' || businessOwnerName.trim() === '' || businessActivity.trim() === '' || establishmentDate.trim() === '' || uaeBank.trim() === '')
                                                    ? 'Company offers require business name, owner, activity, establishment year, and UAE bank.'
                                                    : isRent && (payments.trim() === '' || contractStartDate.trim() === '' || duration.trim() === '')
                                                        ? 'Rent offers require payments, contract start date, and duration.'
                                                        : isSale && buyerType === 'Mortgage Buyer' && (bankName.trim() === '' || preApprovedAmount.trim() === '')
                                                            ? 'Mortgage sale offers require bank name and pre-approved amount.'
                                                            : needsPassport && documents.passport == null
                                                                ? 'Passport file is required.'
                                                                : needsEmiratesId && (documents.emiratesIdFront == null || documents.emiratesIdBack == null)
                                                                    ? 'Emirates ID front and back files are required.'
                                                                    : isCompany && documents.tradeLicense == null
                                                                        ? 'Trade License is required for company offers.'
                                                                        : isSale && documents.depositCheque == null
                                                                            ? 'Deposit Cheque is required for sale offers.'
                                                                            : null
    const submitDisabled = saving || validationError != null || userId == null
    const visibleError = error ?? manualConfigError ?? validationError

    const setFile = (key: keyof ManualOfferDocumentFiles) => (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0] ?? null
        setDocuments((prev) => ({ ...prev, [key]: file }))
        setError(null)
    }

    const handleSubmit = async (): Promise<void> => {
        if (submitDisabled || userId == null) {
            setError(validationError)
            return
        }

        setSaving(true)
        setError(null)
        const result = await dispatch(createManualOffer({
            unit,
            userId,
            amount,
            processStartDate,
            processEndDate,
            clientType,
            documentType,
            firstName,
            lastName,
            country,
            email,
            phone,
            notes,
            occupation,
            employer,
            occupants,
            smoking,
            pets,
            petType,
            inUAESince,
            uaeBank,
            businessName,
            businessOwnerName,
            businessActivity,
            establishmentDate,
            payments,
            contractStartDate,
            duration,
            buyerType,
            bankName,
            bankPreApproval,
            preApprovedAmount,
            purchaseReason,
            documents
        }))

        if (result.isSuccessful) {
            setSaving(false)
            onClose()
            void swalSuccess('Offer has been created.', undefined, 'Success')
            return
        }

        setSaving(false)
        setError(result.error ?? 'Could not create offer.')
    }

    return createPortal(
        <div className="manual-offer__backdrop" onClick={saving ? undefined : onClose}>
            <div className="manual-offer__modal" role="dialog" aria-modal="true" aria-labelledby="manual-offer-title" onClick={(event) => event.stopPropagation()}>
                <header className="manual-offer__header">
                    <div>
                        <h2 id="manual-offer-title">Add Offer</h2>
                        <p>Create a completed offer for the selected unit.</p>
                    </div>
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Close</button>
                </header>

                <div className="manual-offer__body">
                    <section className="manual-offer__unit">
                        <img src={unit.imageUrl} alt={unit.headline} />
                        <div>
                            <div className="manual-offer__unit-title">
                                <strong>{unit.headline}</strong>
                                {listingType != null && <span className="badge badge-light-primary">{listingType}</span>}
                            </div>
                            <span>{unit.propertyName}</span>
                            <small>{unit.areaName}{unit.unitNumber != null ? `, Unit ${unit.unitNumber}` : `, Ref ${unit.unitReference}`}</small>
                            <div className="manual-offer__unit-meta">
                                <span>Ref: {unit.unitReference ?? unit.unitId}</span>
                                <span>Type: {unit.unitType ?? unit.unitCategory ?? '-'}</span>
                                <span>Current price: {formatPrice(unit.currentPrice)}</span>
                            </div>
                        </div>
                    </section>

                    <section className="manual-offer__panel">
                        <h3>Agent / Manual User</h3>
                        <div className="manual-offer__mode-toggle" role="group" aria-label="User source">
                            <button className={userMode === 'existing-user' ? 'active' : ''} type="button" disabled={saving} onClick={() => setUserMode('existing-user')}>
                                Existing User
                            </button>
                            <button className={userMode === 'manual-bso' ? 'active' : ''} type="button" disabled={saving} onClick={() => setUserMode('manual-bso')}>
                                Manual by BSO
                            </button>
                        </div>
                        {userMode === 'existing-user'
                            ? (
                                <ManualUserSelector
                                    users={users}
                                    selectedUserId={selectedUser?.id ?? null}
                                    disabled={saving}
                                    onSelect={(user) => {
                                        setSelectedUser(user)
                                        setError(null)
                                    }}
                                />
                            )
                            : (
                                <section className={`manual-offer__manual-user${manualUserIsConfigured ? '' : ' manual-offer__manual-user--error'}`}>
                                    <strong>Manual by BSO</strong>
                                    <span>Use {manualActionUserConfig.email || 'configured system account'} system account for external/manual operations.</span>
                                    {manualUserIsConfigured
                                        ? <small>User ID {String(manualActionUserConfig.id)}</small>
                                        : <small>Missing REACT_APP_BSO_MANUAL_ACTION_USER_ID.</small>}
                                </section>
                            )}
                    </section>

                    <section className="manual-offer__panel">
                        <h3>Offer Information</h3>
                        <div className="manual-offer__form-grid">
                            <div>
                                <label className="form-label" htmlFor="manual-offer-amount">Offer amount *</label>
                                <input id="manual-offer-amount" className="form-control form-control-sm" type="number" min="1" value={amount} disabled={saving} onChange={(event) => { setAmount(event.target.value); setError(null) }} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-status">Status</label>
                                <input id="manual-offer-status" className="form-control form-control-sm" type="text" value={DEFAULT_MANUAL_OFFER_STATUS} disabled readOnly />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-process-start">Process start date *</label>
                                <input id="manual-offer-process-start" className="form-control form-control-sm" type="date" value={processStartDate} disabled={saving} onChange={(event) => { setProcessStartDate(event.target.value); setError(null) }} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-process-end">Process end date *</label>
                                <input id="manual-offer-process-end" className="form-control form-control-sm" type="date" value={processEndDate} disabled={saving} onChange={(event) => { setProcessEndDate(event.target.value); setError(null) }} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-client-type">Client type *</label>
                                <select id="manual-offer-client-type" className="form-select form-select-sm" value={clientType} disabled={saving} onChange={(event) => { setClientType(event.target.value as ManualOfferClientType); setError(null) }}>
                                    {MANUAL_OFFER_CLIENT_TYPES.map((option) => <option value={option} key={option}>{option}</option>)}
                                </select>
                            </div>
                        </div>
                    </section>

                    <section className="manual-offer__panel">
                        <h3>Payment / Contract</h3>
                        {isRent && (
                            <div className="manual-offer__form-grid">
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-payments">Payment installments *</label>
                                    <select id="manual-offer-payments" className="form-select form-select-sm" value={payments} disabled={saving} onChange={(event) => { setPayments(event.target.value); setError(null) }}>
                                        {MANUAL_OFFER_PAYMENT_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-contract-start">Contract start date *</label>
                                    <input id="manual-offer-contract-start" className="form-control form-control-sm" type="date" value={contractStartDate} disabled={saving} onChange={(event) => { setContractStartDate(event.target.value); setError(null) }} />
                                </div>
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-duration">Contract duration *</label>
                                    <select id="manual-offer-duration" className="form-select form-select-sm" value={duration} disabled={saving} onChange={(event) => { setDuration(event.target.value); setError(null) }}>
                                        {MANUAL_OFFER_DURATION_OPTIONS.map((option) => <option value={option} key={option}>{option} Months</option>)}
                                    </select>
                                </div>
                                <FileInput label="Deposit pay slip" file={documents.depositSlip} disabled={saving} onChange={setFile('depositSlip')} />
                            </div>
                        )}
                        {isSale && (
                            <div className="manual-offer__form-grid">
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-buyer-type">Offer type *</label>
                                    <select id="manual-offer-buyer-type" className="form-select form-select-sm" value={buyerType} disabled={saving} onChange={(event) => { setBuyerType(event.target.value as ManualOfferBuyerType); setError(null) }}>
                                        {MANUAL_OFFER_BUYER_TYPES.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-purchase-reason">Purchase reason</label>
                                    <select id="manual-offer-purchase-reason" className="form-select form-select-sm" value={purchaseReason} disabled={saving} onChange={(event) => setPurchaseReason(event.target.value as 'Investment' | 'Personal use')}>
                                        {MANUAL_OFFER_PURCHASE_REASONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-bank-preapproval">Bank pre-approval</label>
                                    <select id="manual-offer-bank-preapproval" className="form-select form-select-sm" value={bankPreApproval} disabled={saving} onChange={(event) => setBankPreApproval(event.target.value as 'Yes' | 'No')}>
                                        {MANUAL_OFFER_YES_NO_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-bank-name">Bank name{buyerType === 'Mortgage Buyer' ? ' *' : ''}</label>
                                    <select id="manual-offer-bank-name" className="form-select form-select-sm" value={bankName} disabled={saving} onChange={(event) => setBankName(event.target.value)}>
                                        <option value="">Select bank</option>
                                        {bankOptions.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-preapproved-amount">Pre-approved amount{buyerType === 'Mortgage Buyer' ? ' *' : ''}</label>
                                    <input id="manual-offer-preapproved-amount" className="form-control form-control-sm" type="number" min="0" value={preApprovedAmount} disabled={saving} placeholder="Amount" onChange={(event) => setPreApprovedAmount(event.target.value)} />
                                </div>
                                <FileInput label="Deposit Cheque" file={documents.depositCheque} disabled={saving} onChange={setFile('depositCheque')} required />
                            </div>
                        )}
                    </section>

                    <section className="manual-offer__panel">
                        <h3>Client Information</h3>
                        <div className="manual-offer__form-grid">
                            <div>
                                <label className="form-label" htmlFor="manual-offer-first-name">First name *</label>
                                <input id="manual-offer-first-name" className="form-control form-control-sm" type="text" value={firstName} disabled={saving} onChange={(event) => { setFirstName(event.target.value); setError(null) }} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-last-name">Last name *</label>
                                <input id="manual-offer-last-name" className="form-control form-control-sm" type="text" value={lastName} disabled={saving} onChange={(event) => { setLastName(event.target.value); setError(null) }} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-country">Nationality *</label>
                                <select id="manual-offer-country" className="form-select form-select-sm" value={country} disabled={saving} onChange={(event) => { setCountry(event.target.value); setError(null) }}>
                                    <option value="">Select country</option>
                                    {countryOptions.map((option) => <option value={option} key={option}>{option}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-email">Email</label>
                                <input id="manual-offer-email" className="form-control form-control-sm" type="email" value={email} disabled={saving} placeholder="Optional" onChange={(event) => setEmail(event.target.value)} />
                            </div>
                            <div>
                                <label className="form-label" htmlFor="manual-offer-phone">Phone</label>
                                <input id="manual-offer-phone" className="form-control form-control-sm" type="tel" value={phone} disabled={saving} placeholder="Optional" onChange={(event) => setPhone(event.target.value)} />
                            </div>
                            {isIndividual && isRent && (
                                <>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-occupation">Occupation *</label>
                                        <input id="manual-offer-occupation" className="form-control form-control-sm" type="text" value={occupation} disabled={saving} onChange={(event) => { setOccupation(event.target.value); setError(null) }} />
                                    </div>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-employer">Employer *</label>
                                        <input id="manual-offer-employer" className="form-control form-control-sm" type="text" value={employer} disabled={saving} onChange={(event) => { setEmployer(event.target.value); setError(null) }} />
                                    </div>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-occupants">Occupants *</label>
                                        <select id="manual-offer-occupants" className="form-select form-select-sm" value={occupants} disabled={saving} onChange={(event) => { setOccupants(event.target.value); setError(null) }}>
                                            {MANUAL_OFFER_OCCUPANT_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-smoking">Smoking *</label>
                                        <select id="manual-offer-smoking" className="form-select form-select-sm" value={smoking} disabled={saving} onChange={(event) => setSmoking(event.target.value as 'Yes' | 'No')}>
                                            {MANUAL_OFFER_YES_NO_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-pets">Pets *</label>
                                        <select id="manual-offer-pets" className="form-select form-select-sm" value={pets} disabled={saving} onChange={(event) => { setPets(event.target.value as 'Yes' | 'No'); setError(null) }}>
                                            {MANUAL_OFFER_YES_NO_OPTIONS.map((option) => <option value={option} key={option}>{option}</option>)}
                                        </select>
                                    </div>
                                    {pets === 'Yes' && (
                                        <div>
                                            <label className="form-label" htmlFor="manual-offer-pet-type">Pet type *</label>
                                            <input id="manual-offer-pet-type" className="form-control form-control-sm" type="text" value={petType} disabled={saving} onChange={(event) => { setPetType(event.target.value); setError(null) }} />
                                        </div>
                                    )}
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-uae-since">In UAE since *</label>
                                        <input id="manual-offer-uae-since" className="form-control form-control-sm" type="number" min="1900" max={getCurrentYear()} value={inUAESince} disabled={saving} onChange={(event) => { setInUAESince(event.target.value); setError(null) }} />
                                    </div>
                                </>
                            )}
                            {isCompany && (
                                <>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-business-name">Company name *</label>
                                        <input id="manual-offer-business-name" className="form-control form-control-sm" type="text" value={businessName} disabled={saving} onChange={(event) => { setBusinessName(event.target.value); setError(null) }} />
                                    </div>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-business-owner">Business owner name *</label>
                                        <input id="manual-offer-business-owner" className="form-control form-control-sm" type="text" value={businessOwnerName} disabled={saving} onChange={(event) => { setBusinessOwnerName(event.target.value); setError(null) }} />
                                    </div>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-business-activity">Business activity *</label>
                                        <input id="manual-offer-business-activity" className="form-control form-control-sm" type="text" value={businessActivity} disabled={saving} onChange={(event) => { setBusinessActivity(event.target.value); setError(null) }} />
                                    </div>
                                    <div>
                                        <label className="form-label" htmlFor="manual-offer-establishment-date">Establishment year *</label>
                                        <input id="manual-offer-establishment-date" className="form-control form-control-sm" type="number" min="1900" max={getCurrentYear()} value={establishmentDate} disabled={saving} onChange={(event) => { setEstablishmentDate(event.target.value); setError(null) }} />
                                    </div>
                                </>
                            )}
                            {(isCompany || (isIndividual && isRent)) && (
                                <div>
                                    <label className="form-label" htmlFor="manual-offer-uae-bank">Client bank in UAE *</label>
                                    <select id="manual-offer-uae-bank" className="form-select form-select-sm" value={uaeBank} disabled={saving} onChange={(event) => { setUaeBank(event.target.value); setError(null) }}>
                                        <option value="">Select bank</option>
                                        {bankOptions.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="manual-offer__panel">
                        <h3>Client Documents</h3>
                        <div className="manual-offer__form-grid">
                            <div>
                                <label className="form-label" htmlFor="manual-offer-document-type">Document type *</label>
                                <select id="manual-offer-document-type" className="form-select form-select-sm" value={documentType} disabled={saving} onChange={(event) => { setDocumentType(event.target.value as ManualOfferDocumentType); setError(null) }}>
                                    {MANUAL_OFFER_DOCUMENT_TYPES.map((option) => <option value={option} key={option}>{DOCUMENT_TYPE_LABELS[option]}</option>)}
                                </select>
                            </div>
                            {needsPassport && <FileInput label="Passport" file={documents.passport} disabled={saving} onChange={setFile('passport')} required />}
                            {needsEmiratesId && (
                                <>
                                    <FileInput label="Emirates ID front" file={documents.emiratesIdFront} disabled={saving} onChange={setFile('emiratesIdFront')} required />
                                    <FileInput label="Emirates ID back" file={documents.emiratesIdBack} disabled={saving} onChange={setFile('emiratesIdBack')} required />
                                </>
                            )}
                            {isCompany && <FileInput label="Company Trade License" file={documents.tradeLicense} disabled={saving} onChange={setFile('tradeLicense')} required />}
                        </div>
                    </section>

                    <section className="manual-offer__panel">
                        <h3>Related Documents / Notes</h3>
                        <div className="manual-offer__form-grid">
                            {signedDocumentCategory != null && (
                                <FileInput label={`Signed ${signedDocumentCategory}`} file={documents.signedDocument} disabled={saving} onChange={setFile('signedDocument')} />
                            )}
                        </div>
                        <label className="form-label mt-3" htmlFor="manual-offer-notes">Additional Notes</label>
                        <textarea id="manual-offer-notes" className="form-control form-control-sm" value={notes} rows={3} disabled={saving} placeholder="Optional" onChange={(event) => setNotes(event.target.value)} />
                    </section>

                    {visibleError != null && (
                        <div className="alert alert-light-danger mb-0">{visibleError}</div>
                    )}
                </div>

                <footer className="manual-offer__footer">
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="btn btn-sm btn-primary" type="button" onClick={() => { void handleSubmit() }} disabled={submitDisabled}>
                        {saving ? 'Creating...' : 'Create Completed Offer'}
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    )
}
